import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import type { MethodologyResponse } from '@/api/schema/data-contracts';
import { ConfirmationModal } from '@/components/molecules/ConfirmationModal';
import { useMessages } from '@/hooks/messages';
import { useControlledFieldArray } from '@/hooks/react-hook-form/useControlledFieldArray';
import { useConfirmation } from '@/hooks/useConfirmation';
import type { OmitSafely } from '@/types';

import { DeleteEstimationItemModal } from '../../DeleteEstimationItemModal';
import type { EstimationItemsGroupedTableProps } from '../../EstimationItemsGroupedTables';
import EstimationItemsGroupedTables from '../../EstimationItemsGroupedTables';
import { mapFromResourceEstimationItemsTableRecord } from '../../mappers/estimationItemsEditFormModelMapper';
import { estimationTabMessageDefinitions } from '../../messages';
import type {
  EstimationItemActionCallback,
  EstimationItemIsIncludedActionCallback,
  EstimationItemIsIncludedActionPromiseCallback,
  EstimationItemsEditFormModel,
  EstimationItemsFormModelRow,
  RecalculateCallback,
  ResourceEstimationItemsTableRecord,
} from '../../model';
import { OmitModal } from '../../OmitModal';

interface TopLevelEstimationItemsFormProps {
  onSubmit: (
    formModel: EstimationItemsEditFormModel,
    removeEstimationItemFromModelById: EstimationItemActionCallback,
  ) => Promise<void>;
  topLevelEstimationItems: ResourceEstimationItemsTableRecord[];
  methodologies: MethodologyResponse[];
  estimationMethodologyId: string;
  recalculate: RecalculateCallback;
  tableProps: OmitSafely<
    EstimationItemsGroupedTableProps,
    | 'estimationItems'
    | 'control'
    | 'onIncludeClicked'
    | 'onIncludeChanged'
    | 'onMethodologyChange'
    | 'calculatedResultByEstimationItemId'
    | 'formModelEstimationItems'
  >;
}

export const TopLevelEstimationItemsForm: FC<TopLevelEstimationItemsFormProps> = ({
  onSubmit,
  topLevelEstimationItems,
  methodologies,
  estimationMethodologyId,
  recalculate,
  tableProps,
}) => {
  const [estimationItemIdForOmitModal, setEstimationItemIdForOmitModal] = useState<string>();
  const [estimationItemIdForDeleteModal, setEstimationItemIdForDeleteModal] = useState<string>();
  const messages = useMessages(estimationTabMessageDefinitions);

  const confirmation = useConfirmation();
  const formModelInitial = useMemo<EstimationItemsEditFormModel>(
    () => ({
      estimationItems: topLevelEstimationItems.map((x) =>
        mapFromResourceEstimationItemsTableRecord(
          x,
          estimationMethodologyId,
          tableProps.estimationModel,
        ),
      ),
    }),
    [topLevelEstimationItems, estimationMethodologyId, tableProps.estimationModel],
  );

  const { control, handleSubmit, reset, setValue } = useForm<EstimationItemsEditFormModel>({
    mode: 'onChange',
    defaultValues: formModelInitial,
  });

  const { remove, fields } = useControlledFieldArray({ control, name: 'estimationItems' });

  const resetDirtyFieldsState = useCallback(() => {
    reset(undefined, { keepValues: true }); // Retain current values but clear dirty state
  }, [reset]);

  const removeEstimationItemFromModelById = useCallback<EstimationItemActionCallback>(
    (estimationItemId: string) => {
      /**
       * Workaround:
       * There's an issue when removing item from collection when last item has a field with dirty state and form reset with keepDirtyValues:true is called.
       * It then tries to keep value of an item that is no longer maintained in model. As a result, it appends some inconsistent partial field at the end of collection.
       * In order to prevent it from happening, when removing item we firstly simply reset dirty fields state so that when fresh data are fetched after form submit, all the fields are reset (because there's no dirty one anymore).
       */
      resetDirtyFieldsState();
      return remove(fields.findIndex((field) => field.id === estimationItemId));
    },
    [remove, resetDirtyFieldsState, fields],
  );

  useEffect(() => {
    reset(formModelInitial, { keepDirtyValues: true });
  }, [formModelInitial, reset]);

  const calculatedResultByEstimationItemId = useMemo(
    () => recalculate(fields),
    [fields, recalculate],
  );

  const { onItemRename, ...restTableProps } = tableProps;

  const handleItemRename = useCallback(
    async (estimationItemId: string, newName: string) => {
      await onItemRename?.(estimationItemId, newName);
      // Immediately reflect the new name in the form model so the table updates right after the
      // rename tick is confirmed, instead of only after the next full "Update Estimate" submit.
      // The rename mutation also invalidates the query, but the table renders the name from the
      // form model (formModelItem.name) which the keepDirtyValues reset doesn't refresh in time.
      // EWM-739.
      const index = fields.findIndex((field) => field.id === estimationItemId);
      if (index >= 0) {
        setValue(`estimationItems.${index}.name`, newName, { shouldDirty: false });
      }
    },
    [onItemRename, fields, setValue],
  );

  const enrichedOnSubmit = useCallback(
    async (formModel: EstimationItemsEditFormModel) => {
      await onSubmit(formModel, removeEstimationItemFromModelById);
    },
    [onSubmit, removeEstimationItemFromModelById],
  );

  const onBeforeMethodologyChange = useCallback(
    () =>
      confirmation.requestConfirmation(
        messages.methodologyChangeConfirmationModalTitle(),
        messages.methodologyChangeConfirmationModalWarningMessage(),
      ),
    [confirmation, messages],
  );

  const onMethodologyChanged = useCallback(
    async (estimationItemId: string, methodologyId: string | null) => {
      const formModelIndex = topLevelEstimationItems.findIndex((x) => x.id === estimationItemId); // TODO: maybe we should search on formModel instead (because of custom phases)
      const newMethodology = methodologies.find((x) => x.id === methodologyId);
      if (!newMethodology) {
        throw new Error('Methodology not found');
      }
      const newParameters = newMethodology.defaultParameters.map((x) => ({
        parameterName: x.label,
        value: 0,
      }));

      setValue(`estimationItems.${formModelIndex}.parameters`, newParameters);
      await handleSubmit(enrichedOnSubmit)();
      return Promise.resolve(true);
    },
    [setValue, topLevelEstimationItems, handleSubmit, enrichedOnSubmit, methodologies],
  );

  const onIncludeClicked: EstimationItemIsIncludedActionPromiseCallback = async (
    estimationItemId,
    isIncluded,
  ) => {
    // excluding or deleting
    if (!isIncluded) {
      const estimationItem = topLevelEstimationItems.find((x) => x.id === estimationItemId);
      if (!estimationItem) {
        throw new Error('Estimation item not found.');
      }
      // custom estimation item => display delete confirmation modal
      if (!estimationItem.estimationModelItemId) {
        setEstimationItemIdForDeleteModal(estimationItemId);
        return Promise.reject(new Error('Postponing change of the value after confirmation'));
      }
      // required estimation model item => display omit reason modal
      if (estimationItem.isRequired) {
        setEstimationItemIdForOmitModal(estimationItemId);
        return Promise.reject(new Error('Postponing change of the value after confirmation'));
      }
      // omit straight away
      setOmit(fields, estimationItemId, setValue, null);
      return Promise.resolve();
    }
    // including
    const rowIndex = fields.findIndex((x) => x.id === estimationItemId);
    setValue(`estimationItems.${rowIndex}.omitReason`, null);
    return Promise.resolve();
  };

  const onIncludeChanged: EstimationItemIsIncludedActionCallback = async () => {
    await handleSubmit(enrichedOnSubmit)();
  };

  /**
   * The collection of estimation items from BE is normally matched to form model and we reset the form whenever the collection changes.
   * However the reset function is asynchronous and the change isn't immediate. In order to prevent issues when it cannot find form data
   * for a table row, we display only those estimation items that are in the form model.
   */
  const tableItems = topLevelEstimationItems.filter((x) =>
    fields.some((formModelItem) => formModelItem.id === x.id),
  );

  return (
    <>
      <form onSubmit={handleSubmit(enrichedOnSubmit)} onBlur={handleSubmit(enrichedOnSubmit)}>
        <EstimationItemsGroupedTables
          control={control}
          formModelEstimationItems={fields}
          estimationItems={tableItems}
          onIncludeClicked={onIncludeClicked}
          onIncludeChanged={onIncludeChanged}
          onBeforeMethodologyChange={onBeforeMethodologyChange}
          onMethodologyChange={onMethodologyChanged}
          calculatedResultByEstimationItemId={calculatedResultByEstimationItemId}
          {...restTableProps}
          onItemRename={onItemRename ? handleItemRename : undefined}
        />
        {/* The submit button is here, but just hidden so that we can submit form by pressing enter */}
        <input type='submit' className='hidden' />
      </form>
      <OmitModal
        open={!!estimationItemIdForOmitModal}
        onClose={() => {
          setEstimationItemIdForOmitModal(undefined);
        }}
        onConfirm={async (reason) => {
          setOmit(fields, estimationItemIdForOmitModal, setValue, reason);
          setEstimationItemIdForOmitModal(undefined);
          await handleSubmit(enrichedOnSubmit)();
        }}
      />
      <DeleteEstimationItemModal
        open={!!estimationItemIdForDeleteModal}
        onClose={() => {
          setEstimationItemIdForDeleteModal(undefined);
        }}
        onConfirm={async () => {
          setDelete(fields, estimationItemIdForDeleteModal, setValue);
          setEstimationItemIdForDeleteModal(undefined);
          await handleSubmit(enrichedOnSubmit)();
        }}
      />
      <ConfirmationModal {...confirmation.modalProps} />
    </>
  );
};

function setOmit(
  formModelEstimationItems: EstimationItemsFormModelRow[],
  estimationItemId: string | undefined,
  setValue: UseFormSetValue<EstimationItemsEditFormModel>,
  reason: string | null,
) {
  const rowIndex = formModelEstimationItems.findIndex((x) => x.id === estimationItemId);
  setValue(`estimationItems.${rowIndex}.omitReason`, reason);
  setValue(`estimationItems.${rowIndex}.isIncluded`, false);
}

function setDelete(
  formModelEstimationItems: EstimationItemsFormModelRow[],
  estimationItemId: string | undefined,
  setValue: UseFormSetValue<EstimationItemsEditFormModel>,
) {
  const rowIndex = formModelEstimationItems.findIndex((x) => x.id === estimationItemId);
  setValue(`estimationItems.${rowIndex}.shouldDelete`, true);
  setValue(`estimationItems.${rowIndex}.isIncluded`, false);
}
